import { memo } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import pathToRegexp from 'path-to-regexp';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { BellRing, LinkIcon, PlusCircle, Settings, X } from 'lucide-react';
import to from 'await-to-js';

import { notify } from '@/components/toast';
import { Ellipsis } from '@/components/ui/ellipsis/ellipsis';
import {
  ROUTE_WORKFLOW_GROUPS,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import { SpanType, withSpanAsync } from '@/tracing';
import { formatNumber } from '@/utils';
import { cn } from '@/shadcn/lib/utils';

import DownloadFile from './downloadFile';
import SubscriptionsMenu from './subscriptionsMenu';
import mutation from './uploadDocumentationModal/mutation';
import UploadDocumentationModal from './uploadDocumentationModal/uploadDocumentationModal';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shadcn/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';
import { featureFlag } from '@/featureFlag';
import { AddToFavorite } from '@/components/ui/addToFavorite';

export const WorkflowRow = memo(
  ({
    workflow,
    isSuperuser = false,
    updateWorkflowDocumentation,
    userId,
    isFavorite,
    onFavoriteChange
  }) => {
    const {
      rowId,
      canUpdate,
      name,
      traces,
      groups,
      documentation,
      actions: { nodes: actions },
      notificationSubscriptionsUpdated,
      settings
    } = workflow;

    const subscriptionsDisabled =
      settings?.disableSubscriptions === true ||
      notificationSubscriptionsUpdated?.totalCount === 0;

    const deleteWorkflowDocumentation = () => {
      const { url, file } = documentation;
      let documentationName;

      if (url) documentationName = url;
      if (file) documentationName = file.name;

      withSpanAsync(
        'updateWorkflowDocumentation',
        SpanType.processing,
        async () => {
          const promise = updateWorkflowDocumentation({
            variables: { rowId: rowId, url: '', fileDigest: null }
          });

          notify.promise(promise, {
            loading: 'Deleting documentation...',
            success: `${documentationName} documentation deleted`,
            error: `${documentationName} documentation could not be deleted`
          });
          await to(promise);
        }
      );
    };

    const renderDeleteWorkflowDoc = () => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghostDestructiveSubtle"
              data-cy="delete-button"
              type="reset"
              onClick={deleteWorkflowDocumentation}
              aria-label="Click to delete documentation"
            >
              <X />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete documentation</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );

    const renderDocumentation = () => {
      if (documentation?.url) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-muted-foreground flex items-center gap-2">
                  <LinkIcon size={16} />
                  <Ellipsis>
                    <a
                      href={documentation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary hover:underline"
                    >
                      {documentation.url}
                    </a>
                  </Ellipsis>
                  {canUpdate && renderDeleteWorkflowDoc()}
                </div>
              </TooltipTrigger>
              <TooltipContent>Click to open documentation</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      if (documentation?.file) {
        return (
          <div className="text-muted-foreground flex items-center gap-2">
            <DownloadFile data={documentation.file} />
            {canUpdate && renderDeleteWorkflowDoc(0)}
          </div>
        );
      }

      if (!canUpdate) return null;

      return (
        <>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                data-cy="open-workflow-documentation-modal"
                variant="pushtextMuted"
                className="hover:bg-primary hover:text-primary-foreground"
              >
                <PlusCircle />
                Add documentation
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add documentation</DialogTitle>
              </DialogHeader>
              <UploadDocumentationModal workflowRowId={rowId} />
            </DialogContent>
          </Dialog>
        </>
      );
    };

    return (
      <tr
        className="border-border hover:bg-muted border-spacing-1 border-t pb-px text-sm [&>td]:px-4"
        data-cy={`workflow-row-element-${rowId}`}
        data-is-favorite={isFavorite}
      >
        <td className="group/wf w-1/2 max-w-0 min-w-[150px] overflow-hidden">
          <Link
            className="text-card-foreground flex items-center gap-2 no-underline"
            to={pathToRegexp.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: rowId })}
            data-cy={`workflow-row-name-${rowId}`}
          >
            <Ellipsis className="py-[15px] capitalize">{name}</Ellipsis>
            <AddToFavorite
              isFavorite={isFavorite ?? false}
              onChange={value => onFavoriteChange(rowId, value)}
              className={cn(
                'opacity-100 delay-200 duration-100',
                !isFavorite &&
                  'opacity-0 group-hover/wf:opacity-100 group-hover/wf:transition-opacity'
              )}
              dataCy={`add-to-favorite-button-${rowId}`}
            />
          </Link>
        </td>
        <td className="text-left whitespace-nowrap" data-cy="trace-count">
          {formatNumber(traces.totalCount)}
        </td>
        <td className="text-left whitespace-nowrap" data-cy="group-count">
          {formatNumber(groups.totalCount)}
        </td>
        <td className="text-foreground text-xs" data-cy="documentation">
          {renderDocumentation()}
        </td>
        <td className="w-0">
          <div className="flex items-center gap-4">
            {featureFlag.notificationSubscriptions && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={subscriptionsDisabled}
                    data-cy="subscriptions-button"
                    className="hover:text-primary"
                    aria-label="Click to manage notification subscriptions"
                    title="Manage notification subscriptions"
                  >
                    <BellRing />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <SubscriptionsMenu
                    userId={userId}
                    workflowId={workflow.rowId}
                    actions={actions}
                  />
                </DialogContent>
              </Dialog>
            )}

            {canUpdate && isSuperuser && (
              <Link
                className="text-muted-foreground hover:text-primary block"
                to={pathToRegexp.compile(ROUTE_WORKFLOW_GROUPS)({ id: rowId })}
                data-cy="workflow-settings-link"
                title="Go to workflow settings"
              >
                <Settings size={18} />
              </Link>
            )}
          </div>
        </td>
      </tr>
    );
  }
);

WorkflowRow.propTypes = {
  workflow: PropTypes.object.isRequired,
  isSuperuser: PropTypes.bool,
  updateWorkflowDocumentation: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  isFavorite: PropTypes.bool.isRequired,
  onFavoriteChange: PropTypes.func.isRequired
};

export const fragments = {
  workflowRow: gql`
    fragment WorkflowRowFragment on Workflow {
      rowId
      canUpdate
      name
      description
      actions {
        nodes {
          key
          title
        }
      }
      settings
      # https://stratumn.atlassian.net/browse/ENG-248
      # https://github.com/stratumn/trace-api/pull/599
      # workflow.traces calls the workflow_traces() function, which seems to go through all workflow traces.

      # We just want the count in this case, and tracesDeprecated iterates on the raw table which is more optimized.
      # This reduces the user dashboard loading time from ~18s to ~5.5s for admin@stratumn.com on staging,
      # with a couple of workflows with thousands of traces.

      # Making the change in the backend is not possible because workflow.traces as it is
      # is used by cnp-proxy-server to get filtered traces.
      traces: tracesDeprecated {
        totalCount
      }
      groups {
        totalCount
      }
      documentation {
        url
        file {
          name
          size
          digest
        }
      }
      ${
        featureFlag.notificationSubscriptions
          ? `
      notificationSubscriptionsUpdated {
        totalCount
      }
      `
          : ''
      }
    }
  `
};

export default compose(
  graphql(mutation.updateWorkflowDocumentation, {
    name: 'updateWorkflowDocumentation'
  })
)(WorkflowRow);
